/**
 * EM-Chat widget — script unico incluso su tutti i siti EMCOM:
 *   <script src="https://emcom.cloud/widget.js" data-site="emcom-it" defer></script>
 * Nessuna scelta di modello/parametri/system prompt lato client: il widget parla solo
 * con /chat e /widget-config sul backend, che applica la configurazione del sito.
 */

interface WidgetConfig {
  active: boolean;
  label: string;
  ctaLabel: string;
  contactFormUrl: string;
}

interface HistoryItem {
  role: "user" | "assistant";
  content: string;
}

(function initEmChat() {
  const currentScript = document.currentScript as HTMLScriptElement | null;
  const siteSlug = currentScript?.dataset.site;
  if (!siteSlug) {
    console.warn("[em-chat] attributo data-site mancante sullo script, widget non inizializzato.");
    return;
  }
  const apiBase = new URL(currentScript!.src).origin;
  const sessionKey = `em-chat-session-${siteSlug}`;
  let sessionId = localStorage.getItem(sessionKey);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem(sessionKey, sessionId);
  }

  const history: HistoryItem[] = [];
  let panelOpen = false;
  let sending = false;

  fetch(`${apiBase}/widget-config?site=${encodeURIComponent(siteSlug)}`)
    .then((r) => (r.ok ? r.json() : null))
    .then((cfg: WidgetConfig | null) => {
      if (!cfg || !cfg.active) return;
      mount(cfg);
    })
    .catch(() => {
      /* nessun bubble se il config non è raggiungibile: mai rompere il sito ospite */
    });

  function mount(cfg: WidgetConfig): void {
    const root = document.createElement("div");
    root.id = "em-chat-root";
    root.innerHTML = STYLE + markup(cfg);
    document.body.appendChild(root);

    const bubble = root.querySelector<HTMLButtonElement>(".em-bubble")!;
    const panel = root.querySelector<HTMLDivElement>(".em-panel")!;
    const closeBtn = root.querySelector<HTMLButtonElement>(".em-close")!;
    const form = root.querySelector<HTMLFormElement>(".em-form")!;
    const input = root.querySelector<HTMLTextAreaElement>(".em-input")!;
    const messages = root.querySelector<HTMLDivElement>(".em-messages")!;
    const contactLink = root.querySelector<HTMLAnchorElement>(".em-contact-link")!;

    if (cfg.contactFormUrl) {
      contactLink.href = cfg.contactFormUrl;
    } else {
      contactLink.style.display = "none";
    }

    bubble.addEventListener("click", () => {
      panelOpen = !panelOpen;
      panel.classList.toggle("em-open", panelOpen);
      if (panelOpen) input.focus();
    });
    closeBtn.addEventListener("click", () => {
      panelOpen = false;
      panel.classList.remove("em-open");
    });

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      const text = input.value.trim();
      if (!text || sending) return;
      input.value = "";
      autoResize(input);
      appendMessage(messages, "user", text);
      history.push({ role: "user", content: text });
      send(text, apiBase, siteSlug, sessionId!, history, messages, cfg);
    });

    input.addEventListener("input", () => autoResize(input));
    input.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        form.requestSubmit();
      }
    });
  }

  async function send(
    text: string,
    apiBase: string,
    siteSlug: string,
    sessionId: string,
    history: HistoryItem[],
    messages: HTMLDivElement,
    cfg: WidgetConfig
  ): Promise<void> {
    sending = true;
    const assistantEl = appendMessage(messages, "assistant", "");
    assistantEl.classList.add("em-typing");
    assistantEl.textContent = "···";

    let response: Response;
    try {
      response = await fetch(`${apiBase}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          siteSlug,
          sessionId,
          message: text,
          history: history.slice(0, -1).slice(-12),
        }),
      });
    } catch {
      renderFallback(assistantEl, cfg);
      sending = false;
      return;
    }

    if (!response.ok || !response.body) {
      renderFallback(assistantEl, cfg);
      sending = false;
      return;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let assistantText = "";
    let first = true;

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const events = buffer.split("\n\n");
      buffer = events.pop() ?? "";

      for (const raw of events) {
        const eventMatch = raw.match(/^event: (.+)$/m);
        const dataMatch = raw.match(/^data: (.+)$/m);
        if (!eventMatch || !dataMatch) continue;
        const eventName = eventMatch[1];
        let data: any;
        try {
          data = JSON.parse(dataMatch[1]);
        } catch {
          continue;
        }

        if (eventName === "token") {
          if (first) {
            assistantEl.classList.remove("em-typing");
            assistantEl.textContent = "";
            first = false;
          }
          assistantText += data.text;
          assistantEl.textContent = assistantText;
          messages.scrollTop = messages.scrollHeight;
        } else if (eventName === "fallback") {
          renderFallback(assistantEl, cfg, data.message);
        } else if (eventName === "done") {
          history.push({ role: "assistant", content: assistantText });
        }
      }
    }

    sending = false;
  }

  function renderFallback(el: HTMLElement, cfg: WidgetConfig, customMessage?: string): void {
    el.classList.remove("em-typing");
    el.innerHTML = "";
    const p = document.createElement("p");
    p.textContent =
      customMessage ??
      "In questo momento non riesco a risponderti direttamente. Scrivici tramite il modulo di contatto.";
    p.style.margin = "0 0 8px";
    el.appendChild(p);
    if (cfg.contactFormUrl) {
      const a = document.createElement("a");
      a.href = cfg.contactFormUrl;
      a.target = "_blank";
      a.rel = "noopener";
      a.className = "em-fallback-cta";
      a.textContent = cfg.ctaLabel || "Parla con noi";
      el.appendChild(a);
    }
  }

  function appendMessage(container: HTMLDivElement, role: "user" | "assistant", text: string): HTMLDivElement {
    const el = document.createElement("div");
    el.className = `em-msg em-msg-${role}`;
    el.textContent = text;
    container.appendChild(el);
    container.scrollTop = container.scrollHeight;
    return el;
  }

  function autoResize(el: HTMLTextAreaElement): void {
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 120) + "px";
  }

  function markup(cfg: WidgetConfig): string {
    return `
      <button class="em-bubble" aria-label="Apri la chat" type="button">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none"><path d="M4 4h16v12H7l-3 3V4z" fill="currentColor"/></svg>
      </button>
      <div class="em-panel" role="dialog" aria-label="${escapeAttr(cfg.label)}">
        <div class="em-header">
          <div>
            <div class="em-header-title">${escapeHtml(cfg.label)}</div>
            <div class="em-header-sub">Assistente automatico &middot; <a class="em-contact-link" href="#" target="_blank" rel="noopener">${escapeHtml(cfg.ctaLabel)}</a></div>
          </div>
          <button class="em-close" aria-label="Chiudi" type="button">&times;</button>
        </div>
        <div class="em-messages"></div>
        <form class="em-form">
          <textarea class="em-input" rows="1" placeholder="Scrivi un messaggio..." maxlength="2000"></textarea>
          <button class="em-send" type="submit" aria-label="Invia">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M3 11l18-8-8 18-2-8-8-2z" fill="currentColor"/></svg>
          </button>
        </form>
      </div>
    `;
  }

  function escapeHtml(s: string): string {
    return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
  }
  function escapeAttr(s: string): string {
    return escapeHtml(s);
  }

  const STYLE = `<style>
    #em-chat-root, #em-chat-root * { box-sizing: border-box; }
    #em-chat-root { font-family: -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; }
    #em-chat-root .em-bubble {
      position: fixed; right: 20px; bottom: 20px; width: 58px; height: 58px; border-radius: 50%;
      background: linear-gradient(135deg, #7c5cff, #5433d6); color: #fff; border: none; cursor: pointer;
      display: flex; align-items: center; justify-content: center; box-shadow: 0 8px 24px rgba(84,51,214,0.35);
      z-index: 2147483000; transition: transform 0.15s ease;
    }
    #em-chat-root .em-bubble:hover { transform: scale(1.06); }
    #em-chat-root .em-panel {
      position: fixed; right: 20px; bottom: 90px; width: 360px; max-width: calc(100vw - 32px);
      height: 520px; max-height: calc(100vh - 140px); background: #fff; border-radius: 16px;
      box-shadow: 0 16px 48px rgba(20,18,31,0.22); display: flex; flex-direction: column; overflow: hidden;
      opacity: 0; pointer-events: none; transform: translateY(12px) scale(0.98); transition: all 0.18s ease;
      z-index: 2147483000;
    }
    #em-chat-root .em-panel.em-open { opacity: 1; pointer-events: auto; transform: translateY(0) scale(1); }
    #em-chat-root .em-header {
      background: linear-gradient(135deg, #7c5cff, #5433d6); color: #fff; padding: 14px 16px;
      display: flex; align-items: flex-start; justify-content: space-between; gap: 8px;
    }
    #em-chat-root .em-header-title { font-weight: 700; font-size: 0.98rem; }
    #em-chat-root .em-header-sub { font-size: 0.74rem; opacity: 0.85; margin-top: 2px; }
    #em-chat-root .em-header-sub a { color: #fff; text-decoration: underline; }
    #em-chat-root .em-close { background: transparent; border: none; color: #fff; font-size: 1.4rem; cursor: pointer; line-height: 1; padding: 0 2px; }
    #em-chat-root .em-messages { flex: 1; overflow-y: auto; padding: 14px; display: flex; flex-direction: column; gap: 10px; background: #fafafc; }
    #em-chat-root .em-msg { max-width: 82%; padding: 9px 12px; border-radius: 12px; font-size: 0.88rem; line-height: 1.4; white-space: pre-wrap; word-break: break-word; }
    #em-chat-root .em-msg-user { align-self: flex-end; background: #6d4aff; color: #fff; border-bottom-right-radius: 3px; }
    #em-chat-root .em-msg-assistant { align-self: flex-start; background: #eef0f3; color: #1c1f26; border-bottom-left-radius: 3px; }
    #em-chat-root .em-msg-assistant.em-typing { color: #9498a3; letter-spacing: 2px; }
    #em-chat-root .em-fallback-cta { display: inline-block; margin-top: 4px; background: #5433d6; color: #fff; padding: 6px 12px; border-radius: 8px; font-size: 0.82rem; text-decoration: none; }
    #em-chat-root .em-form { display: flex; align-items: flex-end; gap: 8px; padding: 10px; border-top: 1px solid #eceef2; background: #fff; }
    #em-chat-root .em-input { flex: 1; resize: none; border: 1px solid #e3e5ea; border-radius: 10px; padding: 9px 11px; font-size: 0.88rem; font-family: inherit; max-height: 120px; }
    #em-chat-root .em-input:focus { outline: 2px solid #b8a8ff; }
    #em-chat-root .em-send { background: #6d4aff; border: none; color: #fff; width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; cursor: pointer; flex-shrink: 0; }
    #em-chat-root .em-send:hover { background: #5433d6; }
    @media (max-width: 420px) {
      #em-chat-root .em-panel { right: 12px; left: 12px; width: auto; bottom: 84px; }
      #em-chat-root .em-bubble { right: 14px; bottom: 14px; }
    }
  </style>`;
})();
